/**
 * Copy plain text to the clipboard.
 *
 * Prefers the async Clipboard API (navigator.clipboard.writeText) and falls
 * back to a hidden <textarea> + document.execCommand('copy') when it is not
 * available (insecure context, older browsers) or rejects.
 *
 * @returns true when the text was copied, false otherwise.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the legacy fallback below
    }
  }

  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;

  let textarea: HTMLTextAreaElement | null = null;
  try {
    textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    // Guarantee the textarea is removed whether the copy succeeded or threw.
    if (textarea?.parentNode) textarea.parentNode.removeChild(textarea);
  }
}
